import { useSelector, useDispatch } from "react-redux";
import {
  decreaseBookingCount,
  increaseBookingCount,
  removeFromBookingCart,
  setIsBookingCartOpen,
  emptyBookingCart,
  setBookingContactInfo,
  resetBookingState,
} from "../../redux/features/cartStateBooking";
import { useNavigate } from "react-router-dom";
import {
  XCircleIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import axios from "axios";
import { baseURL, socketId } from "configs/axiosConfig";
import Loading from "../../components/Loading/Loading";
import Icon from "shared/Logo/Iconsmall";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";
import { createOrderService } from "api/order/OrderApi";
import { toast } from "react-toastify";
import { FaCalendarCheck, FaConciergeBell, FaTools } from "react-icons/fa";
import ProcessingBar from "../../utils/Processingbar";
import { FaMoneyBillWave, FaCreditCard, FaGlobe } from "react-icons/fa";

const formatBookingTimeRange = (startStr, durationMins = 0) => {
  if (!startStr || typeof startStr !== "string") return null;

  const [datePart, timePart] = startStr.split(" ");
  const dateObj = new Date(`${datePart}T${timePart}`);

  if (isNaN(dateObj.getTime())) return null;

  const endDateObj = new Date(dateObj.getTime() + durationMins * 60000);

  const optionsDate = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const optionsTime = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return {
    dateLabel: dateObj.toLocaleDateString("en-US", optionsDate),
    timeLabel: `${dateObj.toLocaleTimeString(
      "en-US",
      optionsTime
    )} – ${endDateObj.toLocaleTimeString("en-US", optionsTime)}`,
  };
};

const CartBooking = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);

  // CartBooking.jsx
  const bookingCart =
    useSelector((state) => state.cartBooking?.bookingCart) ?? [];

  const bookingStaff =
    useSelector((state) => state.cartBooking?.bookingStaff) ?? [];

  const bookingTime =
    useSelector((state) => state.cartBooking?.bookingTime) ?? " ";

  const bookingPaymentMethod = useSelector(
    (state) => state.cartBooking.bookingPaymentMethod ?? " "
  );

  const isBookingCartOpen = useSelector(
    (state) => state.cartBooking?.isBookingCartOpen
  );
  const previousOperators =
    useSelector((state) => state.cartBooking?.bookingContactInfo) ?? {};

  const [isLoading, setIsLoading] = useState(true);
  const [operatorInfo, setOperatorInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);

  const totalPrice = bookingCart.reduce(
    (total, item) => total + 1 * item.price,
    0
  );

  // fetch operator info for each unique operator id present in bookingCart
  useEffect(() => {
    const fetchOperators = async () => {
      setIsLoading(true);
      const uniqueOperatorIds = [
        ...new Set(bookingCart.map((item) => item.operator)),
      ];

      try {
        const operatorData = await Promise.all(
          uniqueOperatorIds.map(async (id) => {
            if (!operatorInfo[id]) {
              const response = await axios.get(
                `${baseURL}/api/products/operatorInfoinAuthor?operator=${id}`
              );
              return { id, data: response.data.operator };
            }
            return null;
          })
        );

        const updated = {};
        operatorData.forEach((entry) => {
          if (entry) updated[entry.id] = entry.data;
        });

        setOperatorInfo((prev) => ({ ...prev, ...updated }));
      } catch (error) {
        console.error("Error fetching operator data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (bookingCart.length > 0) {
      fetchOperators();
    } else {
      setIsLoading(false);
    }
  }, [bookingCart]);

  // ✅ save all business into booking state contact info
  useEffect(() => {
    dispatch(setBookingContactInfo(operatorInfo));
  }, [operatorInfo, dispatch]);

  // Attach operatorInfo to items
  const cartWithOperator = bookingCart.map((item) => ({
    ...item,
    operatorInfo: operatorInfo[item.operator] || null,
  }));

  // Group by operator
  const groupedItems = cartWithOperator.reduce((acc, item) => {
    if (!acc[item.operator]) acc[item.operator] = [];
    acc[item.operator].push(item);
    return acc;
  }, {});

  const totalPriceByOperator = (cart) =>
    cart.reduce((acc, item) => {
      acc[item.operator] = (acc[item.operator] || 0) + item.price * item.count;
      return acc;
    }, {});
  const totalPrices = totalPriceByOperator(cartWithOperator);

  // online users via socket
  useEffect(() => {
    const userId = user?._id;
    if (userId) {
      socketId.emit("addUser", userId);
      socketId.on("getUsers", (data) => setOnlineUsers(data));
    }
  }, [user]);

  const placeOrderHandler = async () => {
    if (!user) {
      dispatch(setAuthModalOpen(true));
      return;
    }

    const listofOperators = [
      ...new Set(cartWithOperator.map((item) => item.operator)),
    ];

    try {
      const orderData = {
        user: user?._id,
        operator: listofOperators,
        customer_info: {
          email: user?.username,
          name: user?.displayName,
          phone: user?.phone,
        },
        // For booking you might adapt fields (e.g., service, duration, selectedDateTime)
        orderItems: cartWithOperator?.map((item) => ({
          product: item?._id,
          qty: item?.count,
          name: item?.title,
          operator: item?.operator,
          price: item?.price,
          operatorInfo: item?.operatorInfo,
        })),
        totalPrice,
        subTotalPrice: totalPrice,
        // selectedDateTime / professional could be added here per item if you track them
      };

      setLoading(true);
      const order = await createOrderService(orderData);

      listofOperators.forEach((operatorId) => {
        socketId.emit("sendOrderNotification", {
          senderId: user._id,
          receiverId: operatorId,
          orderId: order?.order?._id,
          orderInfo: orderData,
        });
      });

      setLoading(false);

      // ✅ clear booking cart & close panel
      dispatch(emptyBookingCart());
      dispatch(setIsBookingCartOpen(false));

      navigate(`checkout/order/${order?.order?._id}`);
      toast.success("Booking placed successfully!");
    } catch (error) {
      console.error("Booking placement failed:", error);
      toast.error(error || "Something went wrong. Please try again.");
    } finally {
      setTimeout(() => setLoading(false), 2000);
    }
  };

  const checkoutHandler = async () => {
    setLoading(true);
    await new Promise((res) => setTimeout(res, 2000));
    setLoading(false);

    // ✅ close booking tray
    dispatch(setIsBookingCartOpen(false));
    navigate("/checkoutprocess");
  };

  const staffByOperator = bookingStaff.reduce((acc, staff) => {
    if (staff?.operator) {
      if (!acc[staff.operator]) acc[staff.operator] = [];
      acc[staff.operator].push(staff);
    }
    return acc;
  }, {});

  const totalDurationMins = bookingCart.reduce((total, item) => {
    const matchHr = item?.desc?.match(/(\d+)\s*(hr|hour)/i);
    const matchMin = item?.desc?.match(/(\d+)\s*(min|minute)/i);
    const hours = matchHr ? parseInt(matchHr[1], 10) : 0;
    const minutes = matchMin ? parseInt(matchMin[1], 10) : 0;
    return total + hours * 60 + minutes;
  }, 0);

  const formattedBookingTime = formatBookingTimeRange(
    bookingTime,
    totalDurationMins
  );

  const paymentOptions = [
    {
      value: "cash",
      label: "Cash at Kiosk",
      sub: "Pay with cash at pickup",
      icon: <FaMoneyBillWave className="text-xl" />,
    },
    {
      value: "card",
      label: "Card at Kiosk",
      sub: "Pay with card at pickup counter",
      icon: <FaCreditCard className="text-xl" />,
    },
    {
      value: "online",
      label: "Online Payment",
      sub: "Pay securely online via Stripe",
      icon: <FaGlobe className="text-xl" />,
    },
  ];

  const selectedPayment = paymentOptions.find(
    (option) => option.value === bookingPaymentMethod
  );

  return (
    <div
      className={`fixed inset-0 w-full h-full bg-black/40 overflow-y-auto z-[50] auto-scroll ${
        isBookingCartOpen ? "block" : "hidden"
      }`}
      onClick={() => dispatch(setIsBookingCartOpen())}
    >
      <div
        className="fixed right-0 bottom-0 w-full max-w-[600px] h-full bg-white dark:bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 overflow-auto h-full pt-13 border">
          {/* HEADER */}
          <div className="flex justify-between items-center ml-1 mt-3 mb-5 relative">
            <Icon className="w-[40px] m-0 p-0" />
            <div className="relative">
              <h4 className="text-xl font-bold text-secondary-main">BOOKING</h4>
              {cartWithOperator.length > 0 && (
                <span className="absolute -top-2 -right-5 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                  {cartWithOperator.length}
                </span>
              )}
            </div>

            <button
              onClick={() => dispatch(setIsBookingCartOpen())}
              className="text-gray-500 ml-2 hover:text-red-500 transition-colors w-[30px]"
              aria-label="close"
            >
              <XCircleIcon className="max-w-[30px] text-red-500" />
            </button>
          </div>

          {/* LIST */}
          {isLoading ? (
            <Loading />
          ) : (
            <div className="my-2">
              {Object.keys(groupedItems).length === 0 ? (
                <div className="flex flex-col items-center justify-center w-full h-full gap-4">
                  <div className="flex items-center justify-center w-24 h-24 rounded-full border border-textGray text-main">
                    <FaCalendarCheck className="text-4xl text-textGray" />
                  </div>
                  <h1 className="text-sm text-center font-light">
                    Your Booking is empty
                  </h1>
                </div>
              ) : (
                <>
                  {Object.keys(groupedItems).map((operator) => (
                    <div
                      key={operator}
                      className="bg-neutral-100 rounded p-1 dark:bg-neutral-700 border dark:border-neutral-400 mt-2"
                    >
                      <div className="flex justify-between items-center m-2">
                        <div className="flex items-center">
                          <div className="w-6 h-6 border-2 border-gray-300 rounded-full overflow-hidden">
                            <img
                              src={
                                groupedItems[operator][0]?.operatorInfo
                                  ?.photo || ""
                              }
                              alt="Operator Avatar"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h5 className="text-sm font-semibold text-red-400 mx-2">
                            {groupedItems[operator][0]?.operatorInfo?.name ||
                              "Unknown"}
                          </h5>
                        </div>
                      </div>

                      <div className="my-2 mx-3 flex flex-col space-y-1 rounded-xl py-2">
                        {groupedItems[operator].map((item) => (
                          <div
                            key={`${item.title}-${item._id}`}
                            className="border-0 py-1"
                          >
                            <div className="flex justify-between px-0 py-1 items-start md:items-center">
                              <div className="flex items-start lg:items-center">
                                {" "}
                                <div className="flex items-center gap-1 mt-1">
                                  <div className="w-5 h-5 bg-redlight rounded-md flex items-center justify-center ml-0">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3 w-3 text-white"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                <span className="text-primary-contrastText text-sm text-left flex-1 ml-2 max-w-[200px] md:max-w-[400px]">
                                  {item.title}({item.desc})
                                </span>
                              </div>

                              <div className="flex items-center">
                                <span className="text-primary-contrastText text-sm mt-1">
                                  ${item.price}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="bg-neutral-100 rounded p-1 dark:bg-neutral-700 border dark:border-neutral-400 mt-2">
                    <div className="flex justify-between mx-2 my-1 text-gray-500 text-sm dark:text-neutral-300">
                      <span>GST (15% included)</span>
                      <span>
                        {isLoading
                          ? "$0"
                          : `${((totalPrice * 3) / 23).toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between mx-2 my-1 text-gray-500 text-sm dark:text-neutral-300">
                      <span className="text-primary-contrastText text-sm font-bold">
                        Sub-Total
                      </span>
                      <span className="text-primary-contrastText text-sm font-bold">
                        {isLoading ? "$0" : `${totalPrice.toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                  {/* ACTIONS */}
                  <div className="bg-neutral-100 rounded p-1 dark:bg-neutral-700 border dark:border-neutral-400 mt-2">
                    {/* //staff section  */}
                    <div className="my-0 mx-3 flex flex-col space-y-1 rounded-xl py-2">
                      <p className="text-sm text-textGray font-semibold flex items-center gap-2">
                        {" "}
                        Staff
                      </p>{" "}
                      {bookingStaff && bookingStaff.length > 0 && (
                        <div className="flex items-center gap-2 mx-3 mb-2">
                          {bookingStaff.map((staff) => (
                            <div
                              key={staff._id}
                              className="flex items-center gap-2"
                            >
                              <img
                                src={staff.photo}
                                alt={staff.name}
                                className="w-6 h-6 rounded-full object-cover "
                              />
                              <span className="text-sm font-medium text-text">
                                {staff.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>{" "}
                  <div className="bg-neutral-100 rounded p-1 dark:bg-neutral-700 border dark:border-neutral-400 mt-2">
                    {/* time booking section  */}
                    {formattedBookingTime && (
                      <div className="my-2 mx-3 flex flex-col space-y-1 rounded-xl py-2">
                        <p className="text-sm text-textGray font-semibold flex items-center gap-2">
                          Time
                        </p>

                        <div className="flex flex-col ml-4 mt-2">
                          <div className="flex items-center gap-2 mb-1">
                            <FaCalendarCheck className="text-redlight" />
                            <span className="text-sm  text-text">
                              {formattedBookingTime.dateLabel}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaConciergeBell className="text-redlight" />
                            <span className="text-sm  text-text">
                              {formattedBookingTime.timeLabel}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="bg-neutral-100 rounded p-1 dark:bg-neutral-700 border dark:border-neutral-400 mt-2">
                    {/* //staff section  */}
                    <div className="my-0 mx-3 flex flex-col space-y-1 rounded-xl py-2">
                      <p className="text-sm text-textGray font-semibold flex items-center gap-2">
                        Payment Method
                      </p>
                      {selectedPayment ? (
                        <div className="flex items-start gap-3 mt-2">
                          <div className="text-redlight">
                            {selectedPayment.icon}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-text">
                              {selectedPayment.label}
                            </span>
                            <span className="text-xs text-textGray">
                              {selectedPayment.sub}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-textGray italic">
                          Not selected
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 bg-neutral-100 dark:bg-neutral-700 dark:border dark:border-neutral-400 rounded">
                    <div className="flex justify-between mx-2 my-1 text-primary-contrastText text-lg font-bold mt-12 dark:border-b">
                      <span>TOTAL</span>
                      <span>
                        {isLoading ? "$0" : `${totalPrice.toFixed(2)}`}
                      </span>
                    </div>

                    <div className="text-black p-3">
                      <div className="w-full flex justify-center gap-2">
                        <button
                          className="rounded-full bg-gray-400 px-6 py-2 text-lg font-semibold border border-gray-500 shadow"
                          onClick={() => dispatch(resetBookingState())}
                        >
                          <span className="text-white">CLEAR</span>
                        </button>
                        <button
                          className="rounded-full bg-red-500 px-10 py-2 text-lg font-semibold border border-red-500 shadow text-white min-w-[200px]"
                          onClick={checkoutHandler}
                        >
                          {loading ? (
                            <div className="flex items-center">
                              <ProcessingBar />
                            </div>
                          ) : user ? (
                            "Check Out"
                          ) : (
                            "Login to Book"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartBooking;
